import { FC, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface SearchableMultiSelectOption {
  value: string;
  label: string;
  /** Extra text the search also matches against (e.g. an id). */
  keywords?: string;
}

interface SearchableMultiSelectProps {
  options: Array<SearchableMultiSelectOption>;
  selected: Array<string>;
  onChange: (next: Array<string>) => void;
  /** Trigger text; the caller formats "N selected" vs placeholder. */
  triggerLabel: string;
  searchPlaceholder: string;
  emptyText: string;
  clearText: string;
  className?: string;
}

/** Popover with a search box and a checkbox list; copy comes from the caller. */
export const SearchableMultiSelect: FC<SearchableMultiSelectProps> = ({
  options,
  selected,
  onChange,
  triggerLabel,
  searchPlaceholder,
  emptyText,
  clearText,
  className,
}) => {
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();
  const visible = needle
    ? options.filter((option) =>
        `${option.label} ${option.keywords ?? ''}`
          .toLowerCase()
          .includes(needle)
      )
    : options;

  const toggle = (value: string, checked: boolean) => {
    onChange(
      checked ? [...selected, value] : selected.filter((item) => item !== value)
    );
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          size='sm'
          className={cn('h-8 text-xs', className)}>
          {triggerLabel}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align='start'
        className='flex w-72 flex-col gap-2 p-2'>
        <Input
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          placeholder={searchPlaceholder}
          className='h-8 text-xs'
        />
        <ul className='max-h-64 overflow-y-auto'>
          {visible.length === 0 && (
            <li className='text-muted-foreground px-2 py-1.5 text-xs'>
              {emptyText}
            </li>
          )}
          {visible.map((option) => (
            <li key={option.value}>
              <Label className='hover:bg-muted cursor-pointer rounded-md px-2 py-1.5 text-xs font-normal'>
                <Checkbox
                  checked={selected.includes(option.value)}
                  onCheckedChange={(checked) => {
                    toggle(option.value, checked === true);
                  }}
                />
                {option.label}
              </Label>
            </li>
          ))}
        </ul>
        {selected.length > 0 && (
          <Button
            variant='ghost'
            size='sm'
            className='h-7 text-xs'
            onClick={() => {
              onChange([]);
            }}>
            {clearText}
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
};
